import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useMyGroups, useTrip } from '../lib/db';
import { money } from '../lib/money';
import { balances, myUnit } from '../lib/split';
import { applyTheme, loadTheme, type ThemeMode } from '../lib/theme';
import { receiptsEnabled } from '../lib/firebase';
import { Loading, Page, Section, Segmented } from '../components/ui';
import type { Group } from '../types';

export default function You() {
  const { user, signOut } = useAuth();
  const { groups, loading } = useMyGroups(user?.uid);
  const [theme, setTheme] = useState<ThemeMode>(loadTheme);
  const [installable, setInstallable] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);

  // iOS has no install prompt, so tell people where the button actually is.
  useEffect(() => {
    const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    setInstallable(iOS && !standalone);
  }, []);

  return (
    <Page wordmark="You" subtitle={user?.displayName ?? undefined} noFab>
      <div className="card" style={{
        marginTop: 6, padding: 17, display: 'flex', alignItems: 'center', gap: 14
      }}>
        {user?.photoURL
          ? <img src={user.photoURL} alt="" width={44} height={44}
              style={{ borderRadius: '50%', flex: 'none' }} />
          : <span className="uav solo c1"><i>{(user?.displayName ?? '?')[0]}</i></span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>
            {user?.displayName ?? 'Signed in'}
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.email}
          </div>
        </div>
      </div>

      {installable && (
        <div className="banner">
          <div>
            <b>Add Evenhouse to your home screen</b> — tap the Share button in Safari,
            then “Add to Home Screen”. It then opens like any other app, and works offline.
          </div>
        </div>
      )}

      <Section label="Your position by trip" />
      {loading && !groups.length ? <Loading /> : (
        <div className="list">
          {groups.length === 0
            ? <div className="row"><span className="main"><span className="s">No trips yet.</span></span></div>
            : groups.map((g) => <TripRow key={g.id} group={g} uid={user!.uid} />)}
        </div>
      )}

      <Section label="Appearance" />
      <Segmented<ThemeMode>
        value={theme}
        onChange={setTheme}
        options={[{ id: 'light', label: 'Light' }, { id: 'dark', label: 'Dark' }]}
      />

      <Section label="Account" />
      <button className="btn ghost" onClick={() => void signOut()}>Sign out</button>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.55, margin: '16px 2px 0' }}>
        Evenhouse records who owes whom; it never moves money.
        {!receiptsEnabled && ' Receipt photos are turned off in this build.'}
      </p>
    </Page>
  );
}

function TripRow({ group, uid }: { group: Group; uid: string }) {
  const { data } = useTrip(group.id);
  const mine = data ? myUnit(data.units, uid) : undefined;
  const n = data && mine
    ? balances(data.units, data.expenses, data.settlements)[mine.id] ?? 0
    : null;

  return (
    <Link className="row" to={`/t/${group.id}`}>
      <span className="main">
        <span className="t">{group.name}</span>
        <span className="s">{group.when || `${data?.expenses.length ?? 0} expenses`}</span>
      </span>
      <span className="end">
        <span className={`t amt ${n === null ? 'zero' : n > 0 ? 'pos' : n < 0 ? 'neg' : 'zero'}`}>
          {n === null ? '—' : n === 0 ? 'settled' : money(Math.abs(n), group.currency)}
        </span>
        <span className="s">
          {n === null ? 'not in a family yet' : n > 0 ? 'you get back' : n < 0 ? 'you owe' : ''}
        </span>
      </span>
    </Link>
  );
}
